import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowLeft, Calendar, User } from "lucide-react";
import PageCTA from "@/components/PageCTA";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const renderMarkdown = (content) => {
  if (!content) return '';
  return content
    .split('\n')
    .map(line => {
      // Headers
      if (line.startsWith('#### ')) return `<h4 class="font-heading text-lg font-medium text-[var(--color-text-dark)] mt-6 mb-2">${line.slice(5)}</h4>`;
      if (line.startsWith('### ')) return `<h3 class="font-heading text-xl font-medium text-[var(--color-text-dark)] mt-8 mb-3">${line.slice(4)}</h3>`;
      if (line.startsWith('## ')) return `<h2 class="font-heading text-2xl font-medium text-[var(--color-text-dark)] mt-10 mb-4">${line.slice(3)}</h2>`;
      if (line.startsWith('# ')) return `<h1 class="font-heading text-3xl font-medium text-[var(--color-text-dark)] mt-10 mb-4">${line.slice(2)}</h1>`;
      // Lists
      if (line.match(/^[-*] /)) return `<li class="ml-5 text-[var(--color-text-muted)] list-disc">${inlineFormat(line.slice(2))}</li>`;
      if (line.match(/^\d+\. /)) return `<li class="ml-5 text-[var(--color-text-muted)] list-decimal">${inlineFormat(line.replace(/^\d+\.\s/, ''))}</li>`;
      // Empty line
      if (line.trim() === '') return '';
      // Paragraph
      return `<p class="text-[var(--color-text-muted)]">${inlineFormat(line)}</p>`;
    })
    .join('');
};

const inlineFormat = (text) => {
  return text
    .replace(/\*\*(.+?)\*\*/g, '<strong class="font-semibold text-[var(--color-text-dark)]">$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/`(.+?)`/g, '<code class="bg-[var(--color-cream-dark)] px-1.5 py-0.5 rounded text-sm">$1</code>')
    .replace(/\[(.+?)\]\((.+?)\)/g, '<a href="$2" class="text-[var(--color-gold)] hover:underline">$1</a>');
};

const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/blog/${slug}`)
      .then(res => setPost(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [slug]);

  const formatDate = (d) =>
    new Date(d).toLocaleDateString('pl-PL', { day: 'numeric', month: 'long', year: 'numeric' });

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--color-cream)]" style={{ paddingTop: 140 }}>
        <div className="container max-w-3xl mx-auto">
          <div className="animate-pulse space-y-6">
            <div className="h-6 bg-[var(--color-cream-dark)] rounded w-1/4" />
            <div className="h-12 bg-[var(--color-cream-dark)] rounded w-3/4" />
            <div className="aspect-video bg-[var(--color-cream-dark)] rounded" />
          </div>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-[var(--color-cream)] flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-heading text-6xl text-[var(--color-text-dark)] mb-4">404</h1>
          <p className="font-body text-[var(--color-text-muted)] mb-6">Wpis nie został znaleziony.</p>
          <Link to="/blog"><button className="button-primary">Wróć do bloga</button></Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--color-cream)]" data-testid="blog-post-page">
      <article className="container max-w-3xl mx-auto" style={{ paddingTop: 140, paddingBottom: 80 }}>
        <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} className="mb-8">
          <Link to="/blog" className="inline-flex items-center gap-2 text-sm font-body text-[var(--color-text-muted)] hover:text-[var(--color-text-dark)] transition-colors">
            <ArrowLeft className="w-4 h-4" /> Wróć do bloga
          </Link>
        </motion.div>

        <motion.header initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="mb-10">
          <div className="flex flex-wrap items-center gap-4 mb-5">
            <span className="pricing-badge">{post.category}</span>
            <span className="text-xs font-body text-[var(--color-text-muted)] flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5" /> {formatDate(post.created_at)}
            </span>
            <span className="text-xs font-body text-[var(--color-text-muted)] flex items-center gap-1.5">
              <User className="w-3.5 h-3.5" /> {post.author}
            </span>
          </div>
          <h1 className="font-heading text-[clamp(32px,4vw,50px)] text-[var(--color-text-dark)] leading-tight">
            {post.title}
          </h1>
        </motion.header>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="rounded overflow-hidden mb-10">
          <img src={post.image_url} alt={post.title} className="w-full aspect-video object-cover" />
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
          <div
            className="font-body leading-relaxed space-y-4 blog-content"
            dangerouslySetInnerHTML={{ __html: renderMarkdown(post.content) }}
          />
        </motion.div>
      </article>
      <PageCTA />
    </div>
  );
};

export default BlogPost;
